/**
 * Credit Store — balance tracking, settings, and credit event handling.
 *
 * Uses Immer middleware for immutable updates with mutable syntax.
 * enableMapSet() in immerSetup.ts allows direct Map mutations on drafts.
 */

import "./immerSetup";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { EVENTS } from "@/constants/events";
import { ANALYTICS_EVENTS } from "@bash/shared";
import { supabase } from "@/lib/supabase/client";
import { getSocket } from "./socketService";
import { settleMainPending } from "./pendingQueue";
import { useTaskStore } from "./taskStore";
import { resilientFetch } from "@/lib/fetch";
import { posthog } from "@/lib/posthog";
import type {
  CreditUpdatePayload,
  CreditWarningPayload,
  CreditExhaustedPayload,
  CreditSummary,
  CreditApprovalBlock,
  TaskCreditSummary,
  TaskCreditBreakdown,
  BackgroundAgentSource,
} from "@bash/shared";

/** Per-source background spend, all sources defaulted to 0. */
const ZERO_BACKGROUND_BY_SOURCE: Record<BackgroundAgentSource, number> = {
  curator: 0,
  contradiction: 0,
  memory_cards: 0,
  wiki: 0,
  skills: 0,
  brief: 0,
  prep_pack: 0,
  proposal_judge: 0,
  session_summary: 0,
  title_generation: 0,
  notch_summary: 0,
  os_ground: 0,
  routine_scope_gate: 0,
  injection_screen: 0,
  done_verdict: 0,
  delivery_voice: 0,
  sense_gist: 0,
  slack_intro: 0,
};

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

/** Dedupe state for credit_ledger Realtime replays. Lives outside the store
 *  because it's a transport-layer concern, not user-visible state. Size cap
 *  prevents unbounded growth across long-lived sessions. */
const seenLedgerIds = new Set<string>();
const MAX_SEEN_LEDGER_IDS = 500;

// ─── Types ──────────────────────────────────────────────────────────────────

interface CreditStoreState {
  balance: number;
  lifetimeSpent: number;
  lifetimeEarned: number;
  lastRechargeAmount: number;
  spentSinceRecharge: number;
  priorBalance: number;
  todaySpent: number;
  weekSpent: number;
  /** The last 7 days of spend, ascending, last entry = today. Undefined until a
   *  server that computes it answers — the ledger draws no week etching rather
   *  than inventing seven dates. */
  weekByDay?: Array<{ date: string; spent: number }>;
  todayIntelligence: number;
  todayActions: number;
  todayCompute: number;
  weekIntelligence: number;
  weekActions: number;
  weekCompute: number;
  allTimeIntelligence: number;
  allTimeActions: number;
  allTimeCompute: number;
  allTimeOther: number;
  /** The composer's own spend, split in two: the minutes on the Line and the
   *  typed turns. Already carved out of `allTimeIntelligence`/`allTimeActions`
   *  by the SQL function, so the six categories still sum to all-time spend. */
  composerTyped: number;
  composerVoice: number;
  backgroundAgentSpend: number;
  backgroundBySource: Record<BackgroundAgentSource, number>;
  isGracePeriod: boolean;
  graceExpiresAt: Date | null;
  taskCosts: Map<string, number>;
  settings: {
    warnBalance: number;
    confirmToolCost: number;
    confirmTaskCost: number;
    /** The user's own per-turn spend ceiling in USD; null = the platform
     *  default governs. Dollars, unlike the three credit thresholds above. */
    turnSpendCeilingUsd: number | null;
    /** The user's own DAILY spend ceiling in USD; null = the platform default. */
    dailySpendCeilingUsd: number | null;
    /** Per-TASK lifetime budget; null = no per-task limit. */
    maxTaskBudgetUsd: number | null;
  };
  isLoaded: boolean;

  // Warning state
  activeWarning: {
    severity: "low" | "critical" | "grace";
    message: string;
    dismissedAt: number | null;
  } | null;

  showExhaustedModal: boolean;
  exhaustedMessage: string;

  // Per-task credit summaries (from API)
  taskSummaries: TaskCreditSummary[];
  taskSummariesLoaded: boolean;
  taskSummariesTotal: number;
  taskSummariesLoadingMore: boolean;
}

interface CreditStoreActions {
  fetchCredits: () => Promise<void>;
  updateSettings: (
    settings: Partial<{
      warnBalance: number;
      confirmToolCost: number;
      confirmTaskCost: number;
      /** null CLEARS it back to the platform default; the server refuses 0. */
      turnSpendCeilingUsd: number | null;
      /** Same contract for the daily limit. */
      dailySpendCeilingUsd: number | null;
      /** Per-TASK lifetime budget; null = no per-task limit. */
      maxTaskBudgetUsd: number | null;
    }>
  ) => Promise<void>;
  handleCreditUpdate: (payload: CreditUpdatePayload) => void;
  handleCreditWarning: (payload: CreditWarningPayload) => void;
  handleCreditExhausted: (payload: CreditExhaustedPayload) => void;
  respondToCreditApproval: (requestId: string, approved: boolean) => void;
  dismissExhaustedModal: () => void;
  dismissWarning: () => void;
  fetchTaskSummaries: (limit?: number, offset?: number) => Promise<void>;
  fetchTaskBreakdown: (taskId: string) => Promise<TaskCreditBreakdown | null>;
}

type CreditStore = CreditStoreState & CreditStoreActions;

// ─── Helper: get auth token ─────────────────────────────────────────────────

async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useCreditStore = create<CreditStore>()(
  devtools(
    immer((set, _get) => ({
      // Initial state
      balance: 0,
      lifetimeSpent: 0,
      lifetimeEarned: 0,
      lastRechargeAmount: 0,
      spentSinceRecharge: 0,
      priorBalance: 0,
      todaySpent: 0,
      weekSpent: 0,
      weekByDay: undefined,
      todayIntelligence: 0,
      todayActions: 0,
      todayCompute: 0,
      weekIntelligence: 0,
      weekActions: 0,
      weekCompute: 0,
      allTimeIntelligence: 0,
      allTimeActions: 0,
      allTimeCompute: 0,
      allTimeOther: 0,
      composerTyped: 0,
      composerVoice: 0,
      backgroundAgentSpend: 0,
      backgroundBySource: { ...ZERO_BACKGROUND_BY_SOURCE },
      isGracePeriod: false,
      graceExpiresAt: null,
      taskCosts: new Map<string, number>(),
      settings: {
        warnBalance: 50,
        confirmToolCost: 5,
        confirmTaskCost: 50,
        turnSpendCeilingUsd: null,
        dailySpendCeilingUsd: null,
        maxTaskBudgetUsd: null,
      },
      isLoaded: false,
      activeWarning: null,
      showExhaustedModal: false,
      exhaustedMessage: "",
      taskSummaries: [],
      taskSummariesLoaded: false,
      taskSummariesTotal: 0,
      taskSummariesLoadingMore: false,

      // =========================================================================
      // Actions
      // =========================================================================

      fetchCredits: async () => {
        try {
          const token = await getAuthToken();
          if (!token) return;

          const response = await resilientFetch(`${SERVER_URL}/api/credits`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            console.warn(
              "[CreditStore] Failed to fetch credits:",
              response.statusText
            );
            return;
          }

          const data: CreditSummary = await response.json();

          set((state) => {
            state.balance = data.balance;
            state.lifetimeSpent = data.lifetimeSpent;
            state.lifetimeEarned = data.lifetimeEarned;
            state.lastRechargeAmount = data.lastRechargeAmount ?? 0;
            state.spentSinceRecharge = data.spentSinceRecharge ?? 0;
            state.todaySpent = data.todaySpent;
            state.weekSpent = data.weekSpent;
            state.weekByDay = data.weekByDay;
            state.todayIntelligence = data.todayIntelligence ?? 0;
            state.todayActions = data.todayActions ?? 0;
            state.todayCompute = data.todayCompute ?? 0;
            state.weekIntelligence = data.weekIntelligence ?? 0;
            state.weekActions = data.weekActions ?? 0;
            state.weekCompute = data.weekCompute ?? 0;
            // All-time per-category totals drive the COST BREAKDOWN. The HTTP load MUST map these
            // (not only the WS CREDIT_SUMMARY path) — otherwise allTime*=0 while backgroundAgentSpend
            // is real makes the bar show Background=100% until/unless the socket summary lands.
            state.allTimeIntelligence = data.allTimeIntelligence ?? 0;
            state.allTimeActions = data.allTimeActions ?? 0;
            state.allTimeCompute = data.allTimeCompute ?? 0;
            state.allTimeOther = data.allTimeOther ?? 0;
            state.composerTyped = data.composerTyped ?? 0;
            state.composerVoice = data.composerVoice ?? 0;
            state.backgroundAgentSpend = data.backgroundAgentSpend ?? 0;
            state.backgroundBySource = {
              ...ZERO_BACKGROUND_BY_SOURCE,
              ...(data.backgroundBySource ?? {}),
            };
            state.isGracePeriod = data.isGracePeriod;
            state.graceExpiresAt = data.graceExpiresAt
              ? new Date(data.graceExpiresAt)
              : null;
            state.settings = {
              warnBalance: data.settings.warnBalance,
              confirmToolCost: data.settings.confirmToolCost,
              confirmTaskCost: data.settings.confirmTaskCost,
              turnSpendCeilingUsd: data.settings.turnSpendCeilingUsd ?? null,
              dailySpendCeilingUsd: data.settings.dailySpendCeilingUsd ?? null,
              maxTaskBudgetUsd: data.settings.maxTaskBudgetUsd ?? null,
            };
            state.isLoaded = true;
          });
        } catch (err) {
          console.error("[CreditStore] Error fetching credits:", err);
        }
      },

      updateSettings: async (newSettings) => {
        try {
          const token = await getAuthToken();
          if (!token) return;

          const response = await resilientFetch(
            `${SERVER_URL}/api/credits/settings`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(newSettings),
            }
          );

          if (!response.ok) {
            console.warn(
              "[CreditStore] Failed to update settings:",
              response.statusText
            );
            return;
          }

          const data = await response.json();

          set((state) => {
            if (data.warn_balance !== undefined)
              state.settings.warnBalance = data.warn_balance;
            if (data.confirm_tool_cost !== undefined)
              state.settings.confirmToolCost = data.confirm_tool_cost;
            if (data.confirm_task_cost !== undefined)
              state.settings.confirmTaskCost = data.confirm_task_cost;
            // camelCase because that is what PUT /api/credits/settings answers with
            // (credit-routes.handler) — the snake_case keys above predate it.
            if (data.turnSpendCeilingUsd !== undefined) {
              state.settings.turnSpendCeilingUsd = data.turnSpendCeilingUsd;
            }
            if (data.maxTaskBudgetUsd !== undefined) {
              state.settings.maxTaskBudgetUsd = data.maxTaskBudgetUsd;
            }
            if (data.dailySpendCeilingUsd !== undefined) {
              state.settings.dailySpendCeilingUsd = data.dailySpendCeilingUsd;
            }
          });
        } catch (err) {
          console.error("[CreditStore] Error updating settings:", err);
        }
      },

      handleCreditUpdate: (payload: CreditUpdatePayload) => {
        // Dedupe Supabase Realtime replays. ledgerId is the credit_ledger row PK;
        // duplicate delivery would otherwise double-count a deduction in the
        // per-task breakdown. The Set is bounded so it can't grow unbounded
        // across a long-lived session.
        if (payload.ledgerId) {
          if (seenLedgerIds.has(payload.ledgerId)) return;
          seenLedgerIds.add(payload.ledgerId);
          if (seenLedgerIds.size > MAX_SEEN_LEDGER_IDS) {
            const first = seenLedgerIds.values().next().value;
            if (first) seenLedgerIds.delete(first);
          }
        }

        set((state) => {
          // Balance flows through CREDIT_SUMMARY (DB-derived, authoritative).
          // The credit_ledger Realtime path emits balance: null because it has
          // no authoritative balance to convey — only a per-task delta.
          // Apply payload.balance only when it's a real number from a path
          // that owns the balance update.
          if (typeof payload.balance === "number") {
            state.balance = payload.balance;
          }

          if (!payload.taskId) return;

          const idx = state.taskSummariesLoaded
            ? state.taskSummaries.findIndex((t) => t.taskId === payload.taskId)
            : -1;
          const entry = idx !== -1 ? state.taskSummaries[idx] : null;

          // ledger-row path: bump the matching bucket using the SIGNED amount.
          // ledgerAmount < 0 = deduction (bucket -= negative = bucket += |amount|).
          // ledgerAmount > 0 = refund    (bucket -= positive = bucket -= amount).
          // Buckets clamp at 0 so a refund-out-of-order can't go negative on
          // display while DB reconciles.
          if (
            entry &&
            payload.costCategory &&
            payload.ledgerAmount !== undefined
          ) {
            const round3 = (n: number) => Math.round(n * 1000) / 1000;
            const clamp = (n: number) => (n < 0 ? 0 : round3(n));
            const delta = -payload.ledgerAmount;
            if (payload.costCategory === "intelligence") {
              entry.intelligenceCost = clamp(entry.intelligenceCost + delta);
            } else if (payload.costCategory === "actions") {
              entry.actionsCost = clamp(entry.actionsCost + delta);
            } else if (payload.costCategory === "compute") {
              entry.computeCost = clamp(entry.computeCost + delta);
            } else if (payload.costCategory === "background") {
              entry.backgroundCost = clamp((entry.backgroundCost ?? 0) + delta);
            }
            entry.totalCost = round3(
              entry.intelligenceCost +
                entry.actionsCost +
                entry.computeCost +
                (entry.backgroundCost ?? 0)
            );
            entry.lastActivity = new Date().toISOString();
            state.taskCosts.set(payload.taskId, entry.totalCost);
            return;
          }

          // Legacy path: payload carries pre-computed taskCost (no category).
          // Kept for emit sites that haven't migrated to ledger-row plumbing.
          if (payload.taskCost !== undefined) {
            state.taskCosts.set(payload.taskId, payload.taskCost);
            if (entry) {
              entry.totalCost = payload.taskCost;
              entry.lastActivity = new Date().toISOString();
            }
          }
        });
      },

      handleCreditWarning: (payload: CreditWarningPayload) => {
        set((state) => {
          state.balance = payload.balance;
          state.isGracePeriod = payload.severity === "grace";
          state.graceExpiresAt = payload.graceExpiresAt
            ? new Date(payload.graceExpiresAt)
            : null;
          state.activeWarning = {
            severity: payload.severity,
            message: payload.message,
            dismissedAt: null,
          };
        });
      },

      handleCreditExhausted: (payload: CreditExhaustedPayload) => {
        set((state) => {
          state.balance = payload.balance;
          state.showExhaustedModal = true;
          state.exhaustedMessage = payload.message;
        });
      },

      respondToCreditApproval: (requestId: string, approved: boolean) => {
        posthog.capture(ANALYTICS_EVENTS.CREDIT_APPROVAL_RESPONDED, {
          approved,
        });

        // 1. Emit socket event to server
        const socket = getSocket();
        if (socket) {
          socket.emit(EVENTS.CREDIT_PERMISSION_RESPONSE, {
            requestId,
            approved,
          });
        }

        // 2. Update the inline block status and clear pendingInteraction in the task store
        const newStatus: CreditApprovalBlock["status"] = approved
          ? "approved"
          : "denied";
        useTaskStore.getState()._mutateTasks((tasks) => {
          for (const task of tasks.values()) {
            // Check streaming message
            if (task.streamingMessage) {
              for (const block of task.streamingMessage.content) {
                if (
                  block.type === "credit_approval" &&
                  (block as CreditApprovalBlock).requestId === requestId
                ) {
                  (block as CreditApprovalBlock).status = newStatus;
                  if (task.pendingInteraction?.requestId === requestId) {
                    settleMainPending(task);
                  }
                  return;
                }
              }
            }
            // Check committed messages
            for (const msg of task.messages) {
              for (const block of msg.content) {
                if (
                  block.type === "credit_approval" &&
                  (block as CreditApprovalBlock).requestId === requestId
                ) {
                  (block as CreditApprovalBlock).status = newStatus;
                  if (task.pendingInteraction?.requestId === requestId) {
                    settleMainPending(task);
                  }
                  return;
                }
              }
            }
          }
        });
      },

      dismissExhaustedModal: () => {
        set((state) => {
          state.showExhaustedModal = false;
        });
      },

      dismissWarning: () => {
        set((state) => {
          if (state.activeWarning) {
            state.activeWarning.dismissedAt = Date.now();
          }
        });
      },

      fetchTaskSummaries: async (limit = 20, offset = 0) => {
        // Guard concurrent load-more calls so the infinite-scroll sentinel
        // can't fire twice for the same page.
        if (offset > 0 && _get().taskSummariesLoadingMore) return;
        if (offset > 0)
          set((state) => {
            state.taskSummariesLoadingMore = true;
          });
        try {
          const token = await getAuthToken();
          if (!token) return;

          const response = await resilientFetch(
            `${SERVER_URL}/api/credits/tasks?limit=${limit}&offset=${offset}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            console.warn(
              "[CreditStore] Failed to fetch task summaries:",
              response.statusText
            );
            return;
          }

          const data = await response.json();
          const incoming: TaskCreditSummary[] = data.tasks || [];

          set((state) => {
            if (offset === 0) {
              state.taskSummaries = incoming;
            } else {
              // Append, deduping by taskId in case the server's window shifted
              // (new task arrived since the last fetch) or we double-fired.
              const existing = new Set(
                state.taskSummaries.map((t) => t.taskId)
              );
              for (const t of incoming) {
                if (!existing.has(t.taskId)) state.taskSummaries.push(t);
              }
            }
            state.taskSummariesTotal = data.total || 0;
            state.taskSummariesLoaded = true;
          });
        } catch (err) {
          console.error("[CreditStore] Error fetching task summaries:", err);
        } finally {
          if (offset > 0)
            set((state) => {
              state.taskSummariesLoadingMore = false;
            });
        }
      },

      fetchTaskBreakdown: async (taskId: string) => {
        try {
          const token = await getAuthToken();
          if (!token) return null;

          const response = await resilientFetch(
            `${SERVER_URL}/api/credits/task/${taskId}/breakdown`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) return null;

          const breakdown: TaskCreditBreakdown = await response.json();
          return breakdown;
        } catch (err) {
          console.error("[CreditStore] Error fetching task breakdown:", err);
          return null;
        }
      },
    })),
    { name: "CreditStore", enabled: process.env.NODE_ENV === "development" }
  )
);
